// Parsers for the shorthand heatmap.py and rtl_power accept on the command
// line, so a value can be copied straight from either.
//
// Frequency: "902M", "10k", "-125M" (negative allowed: --offset shifts down for
// a downconverter). Duration: "10s", "5m", "2h", "1d". Timestamps:
// "YYYY-MM-DD HH:MM:SS" or epoch seconds.

export class UnitError extends Error {
  override name = 'UnitError'
}

const FREQ_SUFFIXES: Record<string, number> = { '': 1, k: 1e3, K: 1e3, M: 1e6, m: 1e6, G: 1e9, g: 1e9 }
const DURATION_SUFFIXES: Record<string, number> = { '': 1, ms: 0.001, s: 1, m: 60, h: 3600, d: 86400 }

/** `"902M"` / `"10k"` / `"-125M"` -> Hz. Sign is kept. */
export function parseFrequency(value: string): number {
  const match = /^\s*([+-]?\d+(?:\.\d+)?)\s*([kKmMgG]?)(?:[Hh][Zz])?\s*$/.exec(value)
  if (!match) throw new UnitError(`invalid frequency: ${JSON.stringify(value)}`)
  const scale = FREQ_SUFFIXES[match[2] ?? '']
  const hz = Number(match[1]) * (scale ?? 1)
  if (!Number.isFinite(hz)) throw new UnitError(`invalid frequency: ${JSON.stringify(value)}`)
  return hz
}

/** `"10s"` / `"5m"` / `"2h"` / `"800ms"` / `"30"` -> seconds. */
export function parseDuration(value: string): number {
  const match = /^\s*(\d+(?:\.\d+)?)\s*(ms|[smhd])?\s*$/.exec(value)
  if (!match) throw new UnitError(`invalid duration: ${JSON.stringify(value)}`)
  const scale = DURATION_SUFFIXES[match[2] ?? '']
  const seconds = Number(match[1]) * (scale ?? 1)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    throw new UnitError(`invalid duration: ${JSON.stringify(value)}`)
  }
  return seconds
}

export interface NaiveTime {
  year: number
  month: number // 1-12
  day: number
  hour: number
  minute: number
  second: number
}

/**
 * A timestamp as typed into the begin/end controls. Either a naive wall-clock
 * time, resolved later in a zone, or epoch seconds, which needs no zone.
 */
export type TimeInput = { kind: 'naive'; naive: NaiveTime } | { kind: 'epoch'; ms: number }

export function parseTimeInput(value: string): TimeInput {
  const trimmed = value.trim()
  if (/^\d{9,13}$/.test(trimmed)) {
    const n = Number(trimmed)
    return { kind: 'epoch', ms: trimmed.length > 11 ? n : n * 1000 }
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/.exec(trimmed)
  if (!match) throw new UnitError(`invalid timestamp: ${JSON.stringify(value)}`)
  return {
    kind: 'naive',
    naive: {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4] ?? 0),
      minute: Number(match[5] ?? 0),
      second: Number(match[6] ?? 0),
    },
  }
}

export function formatHz(hz: number): string {
  const abs = Math.abs(hz)
  if (abs >= 1e9) return `${trimZeros((hz / 1e9).toFixed(6))} GHz`
  if (abs >= 1e6) return `${trimZeros((hz / 1e6).toFixed(3))} MHz`
  if (abs >= 1e3) return `${trimZeros((hz / 1e3).toFixed(3))} kHz`
  return `${Math.round(hz)} Hz`
}

/** Fixed precision in MHz for readouts, so adjacent bins never print alike. */
export function formatMHz(hz: number, stepHz: number): string {
  const decimals = Math.min(6, Math.max(2, Math.ceil(-Math.log10(stepHz / 1e6))))
  return `${(hz / 1e6).toFixed(decimals)} MHz`
}

function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/\.?0+$/, '') : text
}

export function formatDurationClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total - hours * 3600) / 60)
  return `${hours}:${String(minutes).padStart(2, '0')}`
}
